import styled from "@emotion/styled";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Address } from "components";
import { AddressChangeDialog } from "components/common/AddressChangeDialog";
import { ChangeRequestInfoBox } from "components/common/ChangeRequestInfoBox";
import { ChangeRequest, Location, OrderAddress, Service } from "models";
import React from "react";
import { FormattedMessage, translationKeys, useIntl } from "services";
import { useOrder } from "store";
import { ChangeRequestId, ExtraLocationType } from "types";

const InfoBoxContainer = styled.div({
  paddingTop: "1rem",
});

export const getLatestChangeRequestByName = (
  name: ChangeRequestId,
  changeRequests: ChangeRequest[] | null
) => {
  if (changeRequests) {
    return changeRequests
      .filter((changeRequest) => {
        return changeRequest.name === name;
      })
      .sort((resultA, resultB) => {
        return (
          new Date(resultB.updatedAt).getTime() -
          new Date(resultA.updatedAt).getTime()
        );
      })[0];
  }
};

const locationTypeToChangeRequestType = (
  locationType: ExtraLocationType
): ChangeRequestId => {
  switch (locationType) {
    case "OriginLocation":
      return "ORIGIN_ADDRESS";
    case "DestinationLocation":
      return "DESTINATION_ADDRESS";
    case "ExtraPickupLocation":
      return "EXTRA_STOP_PICKUP";
    case "ExtraDropoffLocation":
      return "EXTRA_STOP_DELIVERY";
    case "SITDeliveryLocation":
      return "SIT_DELIVERY_ADDRESS";
    default:
      // unaccounted for location type
      const _exhaustiveCheck: never = locationType;
      return _exhaustiveCheck;
  }
};

const locationTypeToServiceName = (locationType: ExtraLocationType) => {
  switch (locationType) {
    case "OriginLocation":
    case "ExtraPickupLocation":
      return "Pack";
    case "DestinationLocation":
    case "ExtraDropoffLocation":
      return "Transportation";
    case "SITDeliveryLocation":
      return "SIT Delivery";
    default:
      // unaccounted for location type
      const _exhaustiveCheck: never = locationType;
      return _exhaustiveCheck;
  }
};

const getLatestChangeRequest = (
  locationId: string,
  changeRequests: ChangeRequest<"address">[] | null
) => {
  if (changeRequests) {
    return changeRequests
      .filter((changeRequest) => {
        return changeRequest.changeable?.id === locationId;
      })
      .sort((resultA, resultB) => {
        return (
          new Date(resultB.updatedAt).getTime() -
          new Date(resultA.updatedAt).getTime()
        );
      })[0];
  }
};

const convertLocationInfoIntoAddressForDisplay = (
  location: Location
): OrderAddress => {
  const address = new OrderAddress({
    street1: location.streetAddress,
    street2: location.streetAddress2,
    locality: location.city,
    region: location.state,
    postalCode: location.postalCode,
  });

  return address;
};

const getAddressLabel = (locationType: ExtraLocationType): translationKeys => {
  switch (locationType) {
    case "ExtraPickupLocation":
      return "moveTracker.address.changeRequest.ExtraPickup";
    case "ExtraDropoffLocation":
      return "moveTracker.address.changeRequest.ExtraDelivery";
    case "OriginLocation":
      return "moveTracker.address.changeRequest.Origin";
    case "DestinationLocation":
      return "moveTracker.address.changeRequest.Destination";
    case "SITDeliveryLocation":
      return "moveTracker.address.changeRequest.SitDelivery";
    default:
      return "moveTracker.address.changeRequest.General";
  }
};

const getIsChangeDisabled = (location: Location, services: Service[]) => {
  // if a service using the location is performed, block editing
  const performedStatuses = ["PERFORMED", "READY_FOR_ACCOUNTING"];
  switch (location.type) {
    case "DestinationLocation":
    case "ExtraDropoffLocation":
    case "SITDeliveryLocation":
      const servicesWithEndLocation = services.filter(
        (s) => s.endLocation?.id === location.id
      );
      return servicesWithEndLocation.some(
        (s) => s.status && performedStatuses.includes(s.status)
      );
    case "OriginLocation":
    case "ExtraPickupLocation":
      const servicesWithStartLocation = services.filter(
        (s) => s.startLocation?.id === location.id
      );
      return servicesWithStartLocation.some(
        (s) => s.status && performedStatuses.includes(s.status)
      );
    default:
      false;
  }
};

interface FieldComponentProps {
  changeRequests: ChangeRequest[] | null;
  location: Location;
}

export function FieldComponent({
  changeRequests,
  location,
}: FieldComponentProps) {
  const { formatMessage } = useIntl();
  const [editModalOpen, setEditModalOpen] = React.useState(false);
  const changeRequest = getLatestChangeRequest(location.id!, changeRequests);
  const title = formatMessage({
    id: getAddressLabel(location.type),
  });
  const { services } = useOrder();
  const isChangeDisabled = getIsChangeDisabled(location, services);
  const serviceName = isChangeDisabled
    ? locationTypeToServiceName(location.type)
    : "";
  return (
    <section>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <Typography variant="xxsHeading">{title}</Typography>
          <Typography
            variant="sBody"
            data-testid={`move-tracker-address-${location.type}`}
            data-dd-privacy="mask"
          >
            <Address
              address={convertLocationInfoIntoAddressForDisplay(location)}
            />
          </Typography>
        </div>

        <Box
          sx={{
            display: "flex",
            alignContent: "center",
          }}
        >
          <IconButton
            onClick={() => setEditModalOpen(true)}
            aria-label={`Request ${location.type} Address Change`}
            disabled={isChangeDisabled}
          >
            <BorderColorIcon />
          </IconButton>

          {isChangeDisabled && (
            <Tooltip
              enterTouchDelay={0}
              title={
                <FormattedMessage
                  id="moveTracker.address.changeRequestDisabledTooltip.label"
                  values={{ serviceName: serviceName }}
                />
              }
            >
              <IconButton>
                <HelpIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {changeRequest ? (
        <InfoBoxContainer>
          <ChangeRequestInfoBox changeRequest={changeRequest} />
        </InfoBoxContainer>
      ) : null}
      <AddressChangeDialog
        isOpen={editModalOpen}
        setIsOpen={setEditModalOpen}
        changeRequestType={locationTypeToChangeRequestType(location.type)}
        locationId={location.id}
        title={title}
      />
    </section>
  );
}
